import { createSelector } from 'redux-bundler'

import ms from 'milliseconds'
import createAsyncResourceBundle from 'redux-bundler/dist/create-async-resource-bundle'

import { EMPTY_OBJECT, getDateTime } from '~/src/Lib/Utils'
import { createAppIsReadySelector } from '~/src/Store/utils'

import { calculateSumFromPhaseData } from '../Analytics/utils'
import urls from './urls'

const harvestNotificationsBundle = createAsyncResourceBundle({
  name: 'harvestNotifications',
  actionBaseType: 'HARVEST_NOTIFICATIONS',
  staleAfter: ms.minutes(60),
  retryAfter: ms.seconds(5),
  getPromise: ({ apiFetch, store }) => {
    const { harvest } = store.selectHarvestAnalyticsRaw()
    if (!Number.isInteger(harvest)) {
      return Promise.reject(new Error(`Parameter not set: harvest (${harvest})`))
    }
    const query = store.selectHarvestAnalyticsQueryParams()
    return apiFetch(`/harvests/${harvest}/notifications/`, query)
  },
})

export default {
  ...harvestNotificationsBundle,
  reactHarvestNotificationsFetch: createAppIsReadySelector({
    dependencies: ['selectHarvestNotificationsShouldUpdate', 'selectRouteInfo'],
    resultFn: (shouldUpdate, { pattern }) => {
      if (shouldUpdate && pattern === urls.analytics) {
        return { actionCreator: 'doFetchHarvestNotifications' }
      }
      return undefined
    },
  }),
  selectHarvestNotificationsChart: createSelector(
    'selectHarvestNotifications',
    'selectHarvests',
    'selectPhasesByHarvest',
    (harvestNotifications, harvests, phasesByHarvest) => {
      if (!harvestNotifications || !harvests[harvestNotifications.id] || !phasesByHarvest[harvestNotifications.id]) {
        return EMPTY_OBJECT
      }
      const { id: harvestId, currentNotifications, historicalNotifications } = harvestNotifications
      const harvest = harvests[harvestId]
      const maxNotifications = [
        ...Object.values(currentNotifications).flat(),
        ...Object.values(historicalNotifications).flat(),
      ].reduce((max, { y }) => Math.max(max, y), 0)
      const domain = {
        x: [getDateTime(harvest.startDate).toJSDate(), getDateTime(harvest.endDate).toJSDate()],
        y: [0, maxNotifications],
      }
      const data = { current: [], historical: [] }

      let currentPhaseType = ''
      let phaseStartOffset = null
      let phaseTypeStartDate = null
      phasesByHarvest[harvestId].forEach(({ id, phaseType, startDate, totalDays }) => {
        if (phaseType !== currentPhaseType) {
          currentPhaseType = phaseType
          phaseStartOffset = 0
          phaseTypeStartDate = startDate
        }
        const completedDuringPhase = ({ x }) => x >= phaseStartOffset && x < phaseStartOffset + totalDays
        const augmentData = datum => ({
          ...datum,
          x: getDateTime(phaseTypeStartDate).plus({ days: datum.x }).toJSDate(),
          phase: id,
          phaseType,
        })
        const sortByX = (a, b) => a.x - b.x
        data.current = data.current.concat(
          currentNotifications[phaseType].filter(completedDuringPhase).map(augmentData).sort(sortByX),
        )
        data.historical = data.historical.concat(
          historicalNotifications[phaseType].filter(completedDuringPhase).map(augmentData).sort(sortByX),
        )
        phaseStartOffset += totalDays
      })

      return { data, domain }
    },
  ),
  selectHarvestNotificationsByPhase: createSelector(
    'selectPhasesByHarvest',
    'selectHarvestNotifications',
    (phasesByHarvest, harvestNotifications) => {
      if (!harvestNotifications || !phasesByHarvest[harvestNotifications.id]) {
        return EMPTY_OBJECT
      }
      const { id: harvestId, currentNotifications, historicalNotifications } = harvestNotifications
      const phases = phasesByHarvest[harvestId]
      const maxNotifications = [
        ...Object.values(currentNotifications).flat(),
        ...Object.values(historicalNotifications).flat(),
      ].reduce((max, { y }) => Math.max(max, y), 0)

      let currentPhaseType = ''
      let phaseStart = null
      let stageStartDate = null
      return phases.reduce((nbp, { id, phaseType, startDate, totalDays }) => {
        if (phaseType !== currentPhaseType) {
          currentPhaseType = phaseType
          phaseStart = 0
          stageStartDate = startDate
        }
        const nextPhaseStart = phaseStart + totalDays
        const triggeredDuringPhase = ({ x }) => x >= phaseStart && x < nextPhaseStart

        const phase = {
          id,
          domain: { x: [phaseStart, nextPhaseStart - 1], y: [0, maxNotifications] },
          currentNotifications: currentNotifications[phaseType].filter(triggeredDuringPhase),
          historicalNotifications: historicalNotifications[phaseType]
            .filter(triggeredDuringPhase)
            .sort((a, b) => a.x - b.x),
          stageStartDate,
        }

        phaseStart = nextPhaseStart
        return { ...nbp, [id]: phase }
      }, EMPTY_OBJECT)
    },
  ),
  selectHarvestAlertsTriggered: createSelector('selectHarvestNotifications', harvestNotifications =>
    harvestNotifications
      ? {
          current: calculateSumFromPhaseData(harvestNotifications.currentNotifications),
          historical: calculateSumFromPhaseData(harvestNotifications.historicalNotifications),
        }
      : EMPTY_OBJECT,
  ),
}
